import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, KeyRound, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChangePasswordModal = ({ onClose }) => {
  const [step, setStep] = useState("request");
  const [sending, setSending] = useState(false);
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [sentTo, setSentTo] = useState("");

  const requestOtp = async () => {
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/auth/password/request-otp`);
      setSentTo(data.sent_to || "");
      setStep("confirm");
      toast.success("Code sent to your email.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send code.");
    } finally {
      setSending(false);
    }
  };

  const confirm = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setSending(true);
    try {
      await axios.post(`${API}/auth/password/confirm-otp`, { otp: otp.trim(), new_password: pw });
      toast.success("Password changed successfully.");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="change-password-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative mx-auto w-full max-w-md rounded-2xl bg-white p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50"><KeyRound className="h-4 w-4 text-blaze" /></span>
            <h3 className="font-display text-lg font-bold text-slate-900">Change My Password</h3>
          </div>
          <button onClick={onClose} className="rounded-full bg-slate-100 p-2" data-testid="change-pw-close"><X className="h-4 w-4 text-slate-600" /></button>
        </div>

        {step === "request" ? (
          <div className="mt-5 space-y-4">
            <p className="text-sm text-slate-600">We'll email a 6-digit verification code to your account. Enter it on the next step to set a new password.</p>
            <button onClick={requestOtp} disabled={sending} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blaze px-4 py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-60" data-testid="change-pw-request-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send me a code
            </button>
          </div>
        ) : (
          <div className="mt-5 space-y-4">
            {sentTo ? <p className="text-sm text-slate-600">Code sent to <span className="font-semibold text-slate-900">{sentTo}</span>. It expires in 10 minutes.</p> : null}
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">Verification code</label>
              <input value={otp} onChange={(e) => setOtp(e.target.value)} inputMode="numeric" maxLength={6} placeholder="6-digit code" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm tracking-widest" data-testid="change-pw-otp-input" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-slate-500">New password</label>
              <input value={pw} onChange={(e) => setPw(e.target.value)} type="text" placeholder="At least 6 characters" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid="change-pw-new-input" />
            </div>
            <div className="flex gap-2">
              <button onClick={requestOtp} disabled={sending} className="rounded-xl bg-slate-100 px-4 py-3 text-xs font-bold uppercase tracking-widest text-slate-600 disabled:opacity-60" data-testid="change-pw-resend-btn">Resend</button>
              <button onClick={confirm} disabled={sending} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blaze px-4 py-3 text-xs font-bold uppercase tracking-widest text-white disabled:opacity-60" data-testid="change-pw-confirm-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Change password
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
